import { motion } from 'framer-motion'
import {
  Star,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Sparkles,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ReviewAnalysis } from '../../types'
import { APP_NAME, BRAND, CONSULTATION_URL } from '../../config'
import LogoIcon from '../../assets/landing/logo-icon.svg'
import {
  HeaderMetaChip,
  ReportSectionCard,
  ReportSectionHeader,
  ReportSectionBody,
} from './ReportSectionCard'

function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'md' }) {
  const cls = size === 'md' ? 'w-3.5 h-3.5' : 'w-3 h-3'
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          className={`${cls} ${i <= Math.round(rating) ? 'fill-[#fcc800] text-[#fcc800]' : 'fill-[#e5e7eb] text-[#e5e7eb]'}`}
        />
      ))}
    </div>
  )
}

function sentimentTheme(sentiment: string): {
  bar: string
  fill: string
  text: string
  dot: string
  panel: string
} {
  switch (sentiment) {
    case 'Very Positive':
      return {
        bar: 'bg-[#dcfce7]',
        fill: 'bg-[#00a63e]',
        text: 'text-[#008236]',
        dot: 'bg-[#00a63e]',
        panel: 'border-[#b9f8cf] bg-[#f0fdf4]',
      }
    case 'Positive':
      return {
        bar: 'bg-[#dcfce7]',
        fill: 'bg-[#00c950]',
        text: 'text-[#008236]',
        dot: 'bg-[#00c950]',
        panel: 'border-[#b9f8cf] bg-[#f0fdf4]',
      }
    case 'Mixed':
      return {
        bar: 'bg-[#fef3c6]',
        fill: 'bg-[#d08700]',
        text: 'text-[#a65f00]',
        dot: 'bg-[#d08700]',
        panel: 'border-[#fde68a] bg-[#fffbeb]',
      }
    case 'Needs Attention':
      return {
        bar: 'bg-[#ffedd4]',
        fill: 'bg-[#f54900]',
        text: 'text-[#ca3500]',
        dot: 'bg-[#f54900]',
        panel: 'border-[#ffd6a7] bg-[#fff7ed]',
      }
    case 'Critical':
      return {
        bar: 'bg-[#ffe2e2]',
        fill: 'bg-[#ef042d]',
        text: 'text-[#c10007]',
        dot: 'bg-[#ef042d]',
        panel: 'border-[#ffc9c9] bg-[#fef2f2]',
      }
    default:
      return {
        bar: 'bg-[#f3f4f6]',
        fill: 'bg-[#99a1af]',
        text: 'text-[#6a7282]',
        dot: 'bg-[#99a1af]',
        panel: 'border-[#e5e7eb] bg-[#f9fafb]',
      }
  }
}

interface ReviewIntelligenceSectionProps {
  analysis: ReviewAnalysis
  averageRating: number
  totalCount: number
}

export function ReviewIntelligenceSection({
  analysis,
  averageRating,
  totalCount,
}: ReviewIntelligenceSectionProps) {
  const { t } = useTranslation('report')
  const theme = sentimentTheme(analysis.overall_sentiment)
  const scorePct = Math.min(Math.max((analysis.sentiment_score / 10) * 100, 0), 100)

  const hasIssues =
    (analysis.negative_themes?.length ?? 0) > 0 ||
    ['Mixed', 'Needs Attention', 'Critical'].includes(analysis.overall_sentiment)

  return (
    <ReportSectionCard delay={0.22}>
      <ReportSectionHeader
        icon={Sparkles}
        accentColor="#d08700"
        title={t('reviews.intelligence')}
        subtitle={t('reviews.aiAnalysis', { count: analysis.reviewed_count })}
        titleId="review-intelligence-heading"
        right={
          averageRating > 0 ? (
            <HeaderMetaChip>
              <div className="flex items-center gap-2.5">
                <span className="text-[34px] font-bold tabular-nums leading-none text-[#1e2939]">
                  {averageRating.toFixed(1)}
                </span>
                <StarRating rating={averageRating} size="md" />
                <p className="text-xs text-[#99a1af]">
                  · {totalCount.toLocaleString()} {t('reviews.total')}
                </p>
              </div>
            </HeaderMetaChip>
          ) : undefined
        }
      />

      <ReportSectionBody>
        {/* Sentiment meter */}
        <div className={`rounded-[14px] border px-4 py-4 ${theme.panel}`}>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className={`h-2 w-2 shrink-0 rounded-full ${theme.dot}`} />
              <span className={`text-sm font-semibold ${theme.text}`}>
                {analysis.overall_sentiment}
              </span>
              <span className="text-xs text-[#99a1af]">
                {t('reviews.sentimentScore', { score: analysis.sentiment_score })}
              </span>
            </div>
            <span className={`text-xs font-bold tabular-nums ${theme.text}`}>
              {analysis.sentiment_score}/10
            </span>
          </div>
          <div className={`h-1.5 overflow-hidden rounded-full ${theme.bar}`}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${scorePct}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className={`h-full rounded-full ${theme.fill}`}
            />
          </div>
        </div>

        {/* Themes */}
        {((analysis.positive_themes?.length ?? 0) > 0 ||
          (analysis.negative_themes?.length ?? 0) > 0) && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {(analysis.positive_themes?.length ?? 0) > 0 && (
              <div className="rounded-[14px] border border-[#f3f4f6] bg-[#fafafa] p-4">
                <div className="mb-3 flex items-center gap-1.5">
                  <ThumbsUp className="h-3.5 w-3.5 text-[#00a63e]" />
                  <span className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#99a1af]">
                    {t('reviews.positive')}
                  </span>
                </div>
                <ul className="space-y-2">
                  {analysis.positive_themes!.map((th, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#364153]">
                      <CheckCircle2
                        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#00a63e]"
                        strokeWidth={2}
                      />
                      <span className="leading-snug">{th}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(analysis.negative_themes?.length ?? 0) > 0 && (
              <div className="rounded-[14px] border border-[#f3f4f6] bg-[#fafafa] p-4">
                <div className="mb-3 flex items-center gap-1.5">
                  <ThumbsDown className="h-3.5 w-3.5 text-[#ef042d]" />
                  <span className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#99a1af]">
                    {t('reviews.negative')}
                  </span>
                </div>
                <ul className="space-y-2">
                  {analysis.negative_themes!.map((th, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#364153]">
                      <XCircle
                        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#ef042d]"
                        strokeWidth={2}
                      />
                      <span className="leading-snug">{th}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Response rate */}
        {analysis.response_rate_note && (
          <div className="flex items-start gap-3 rounded-[14px] bg-[#f2f2f6] px-4 py-3">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white">
              <MessageSquare className="h-3.5 w-3.5 text-[#99a1af]" />
            </div>
            <p className="text-xs leading-relaxed text-[#4a5565]">{analysis.response_rate_note}</p>
          </div>
        )}

        {/* Recommended actions */}
        {(analysis.suggestions?.length ?? 0) > 0 && (
          <div>
            <p className="mb-3 text-[10px] font-bold uppercase tracking-[1.2px] text-[#99a1af]">
              {t('reviews.recommended')}
            </p>
            <ol className="space-y-2">
              {analysis.suggestions!.map((s, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 rounded-[12px] bg-[#f9fafb] px-3 py-2.5 text-sm leading-snug text-[#364153]"
                >
                  <span
                    className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                    style={{ backgroundColor: BRAND.navy }}
                  >
                    {i + 1}
                  </span>
                  {s}
                </li>
              ))}
            </ol>
          </div>
        )}
      {/* Cartley CTA */}
      {hasIssues && (
        <div
          className="flex items-start gap-3 rounded-[14px] border px-4 py-4"
          style={{ borderColor: `${BRAND.red}30`, backgroundColor: `${BRAND.red}08` }}
        >
            <img src={LogoIcon} alt="logo" className="h-7 w-7 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="mb-1 text-xs font-bold text-[#01114f]">
                {t('sectionCallout.title', { appName: APP_NAME })}
              </p>
              <p className="mb-2 text-xs leading-relaxed text-[#4a5565]">
                {t('callout.reviews.message', { appName: APP_NAME })}
              </p>
              <a
                href={CONSULTATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-bold text-[#ef042d] transition-opacity hover:opacity-90"
              >
                {t('callout.reviews.cta')}
              </a>
            </div>
          </div>
      )}
      </ReportSectionBody>
    </ReportSectionCard>
  )
}

/** Minimal fallback when AI analysis is unavailable but rating exists. */
export function ReviewRatingFallback({
  averageRating,
  totalCount,
}: {
  averageRating: number
  totalCount: number
}) {
  const { t } = useTranslation('report')

  return (
    <ReportSectionCard delay={0.22}>
      <ReportSectionHeader
        icon={Star}
        accentColor="#d08700"
        title={t('reviews.customerTitle')}
        right={
          <HeaderMetaChip>
            <div className="flex items-center gap-3">
              <span className="text-xl font-bold tabular-nums text-[#1e2939]">
                {averageRating.toFixed(1)}
              </span>
              <StarRating rating={averageRating} size="md" />
              <span className="text-sm text-[#99a1af]">
                · {totalCount.toLocaleString()} {t('reviews.total')}
              </span>
            </div>
          </HeaderMetaChip>
        }
      />
    </ReportSectionCard>
  )
}
